import { PDFDocument } from 'pdf-lib';
import apiClient from './apiClient';
import { TEST_CODES } from '../constants/testCodes';
import type {
  ReportArtifactSummary, ReportCacheStatus, ReportArchiveRollup,
  ReportArchiveEntry, ReportArtifactKind,
} from '../types/reportArtifact';

/** One skipped entry from the bulk test-sheet endpoint (BUG-2026-0161):
 *  either the test type has no sheet def, or that sheet failed to render. */
export interface SkippedSheet {
  key: string;
  testName: string;
  reason: string;
}

/** Selections that share a batch-scoped sheet (e.g. Point Load — every
 *  specimen row is its own test, but the sheet is the one work-order batch
 *  grid): one render covered them all. */
export interface CombinedSheets {
  count: number;
  testNames: string[];
}

export interface BulkSheetResult {
  generated: number;
  skipped: SkippedSheet[];
  combined: CombinedSheets | null;
}

/**
 * BUG-2026-0161 — batch-print lab test sheets. POSTs the selected
 * WorkOrderDetail keys and receives ONE merged PDF (each test's bench sheet,
 * one after another) which is opened in a new tab ready for Ctrl+P; if the
 * popup is blocked it falls back to a download. Skipped tests (types without
 * a sheet, per-test failures) come back in the X-Sheets-Skipped header.
 */
export const downloadBulkTestSheets = async (
  workOrderDetailKeys: string[],
): Promise<BulkSheetResult> => {
  let response;
  try {
    response = await apiClient.post(
      '/workorderdetails/bulk-sheet',
      { workOrderDetailKeys },
      // 5 min — a big multi-type batch renders many PDFs server-side before
      // the first byte comes back (BUG-2026-0161 rework).
      { responseType: 'blob', timeout: 300000 },
    );
  } catch (err: any) {
    // With responseType 'blob' the error body arrives as a Blob — surface the
    // server's JSON message (e.g. "no sheets for the selected tests").
    const data = err?.response?.data;
    if (data instanceof Blob) {
      try {
        const parsed = JSON.parse(await data.text());
        if (parsed?.error) throw new Error(parsed.error);
      } catch (inner) {
        if (inner instanceof Error && inner.message && !(inner instanceof SyntaxError)) throw inner;
      }
    }
    throw err;
  }

  const blob = new Blob([response.data], { type: 'application/pdf' });
  const url = window.URL.createObjectURL(blob);
  const opened = window.open(url, '_blank');
  if (!opened) {
    const link = document.createElement('a');
    link.href = url;
    const disposition = response.headers['content-disposition'];
    const match = disposition?.match(/filename="(.+)"/);
    link.download = match?.[1] || 'test-sheets.pdf';
    document.body.appendChild(link);
    link.click();
    link.remove();
  }
  // Give the new tab time to load the blob before revoking.
  setTimeout(() => window.URL.revokeObjectURL(url), 60000);

  let skipped: SkippedSheet[] = [];
  const skippedHeader = response.headers['x-sheets-skipped'];
  if (skippedHeader) {
    try { skipped = JSON.parse(skippedHeader); } catch { /* header is advisory */ }
  }
  let combined: CombinedSheets | null = null;
  const combinedHeader = response.headers['x-sheets-combined'];
  if (combinedHeader) {
    try { combined = JSON.parse(combinedHeader); } catch { /* header is advisory */ }
  }
  const generated = Number(response.headers['x-sheets-generated'] ?? 0);
  return { generated, skipped, combined };
};

export interface SummaryTableResult {
  /** Tests of a supported type that produced no row (no data yet). */
  skipped: number;
  filename: string;
}

/**
 * Summary Table Report — consolidated per-test-type Excel workbook for a
 * project or one work order (one sheet per test type: BTS / Direct Shear /
 * TCS / UCS / UCS with modulus; one row per tested sample, client-fill
 * metadata columns left blank). Downloads the .xlsx to disk.
 */
export const downloadSummaryTable = async (
  scope: { projectKey?: string; workOrderKey?: string },
): Promise<SummaryTableResult> => {
  let response;
  try {
    response = await apiClient.get('/summary-table', {
      params: scope,
      responseType: 'blob',
      // A big project fans out one data fetch per test server-side.
      timeout: 300000,
    });
  } catch (err: any) {
    // With responseType 'blob' the error body arrives as a Blob — surface the
    // server's JSON message (e.g. "no eligible tests in scope").
    const data = err?.response?.data;
    if (data instanceof Blob) {
      try {
        const parsed = JSON.parse(await data.text());
        if (parsed?.error) throw new Error(parsed.error);
      } catch (inner) {
        if (inner instanceof Error && inner.message && !(inner instanceof SyntaxError)) throw inner;
      }
    }
    throw err;
  }

  const disposition = response.headers['content-disposition'];
  const match = disposition?.match(/filename="(.+)"/);
  const filename = match?.[1] || 'summary-tables.xlsx';
  saveBlobToDisk(
    new Blob([response.data], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    }),
    filename,
  );
  return { skipped: Number(response.headers['x-summary-skipped'] ?? 0), filename };
};

export interface FetchedReport {
  blob: Blob;
  filename: string;
  /** How the server satisfied the request (X-Report-Cache) — null on older servers. */
  cache: ReportCacheStatus | null;
}

/**
 * Fetch a test's report PDF as a blob (Preview & Edit dialog + download path).
 * `fresh: true` forces a server-side re-render, bypassing the report archive
 * cache (the re-render is still archived).
 */
export const fetchReportPdf = async (
  workOrderDetailKey: string,
  opts?: { fresh?: boolean },
): Promise<FetchedReport> => {
  const response = await apiClient.get(
    `/workorderdetails/${workOrderDetailKey}/report`,
    {
      responseType: 'blob',
      timeout: 60000,
      params: opts?.fresh ? { fresh: 1 } : undefined,
    },
  );
  const blob = new Blob([response.data], { type: 'application/pdf' });
  const disposition = response.headers['content-disposition'];
  const match = disposition?.match(/filename="(.+)"/);
  return {
    blob,
    filename: match?.[1] || 'report.pdf',
    cache: (response.headers['x-report-cache'] as ReportCacheStatus | undefined) ?? null,
  };
};

/**
 * Merge report PDFs into ONE document, pages in the given order (bulk
 * "Generate Reports" — the lab wants a single giant PDF, not a ZIP).
 */
export const mergePdfBlobs = async (blobs: Blob[]): Promise<Blob> => {
  const merged = await PDFDocument.create();
  for (const blob of blobs) {
    const src = await PDFDocument.load(await blob.arrayBuffer(), { ignoreEncryption: true });
    const pages = await merged.copyPages(src, src.getPageIndices());
    pages.forEach((p) => merged.addPage(p));
  }
  const bytes = await merged.save();
  return new Blob([bytes as BlobPart], { type: 'application/pdf' });
};

const sanitizeFilenamePart = (s: string): string =>
  s.replace(/[^A-Za-z0-9._-]+/g, '-').replace(/^-+|-+$/g, '');

/** Short per-test-type filename tokens, keyed by the display name that reaches
 *  this merge step. BUG-2026-0197: the app now DISPLAYS the Test Catalog item's
 *  name (single source of truth), so both the legacy lab `Test.TestName` values
 *  AND their current catalog names map to the same token; unknown types fall
 *  back to the sanitized full name. Purely cosmetic (the merged-download
 *  filename) — never dispatch. */
const TEST_TYPE_SHORT: Record<string, string> = {
  // Legacy lab Test.TestName values (still surface where a test has no mapping).
  'Direct Shear Test': 'DS',
  'Rock Brazilian Tensile Strength': 'BTS',
  'Rock Triaxial Compressive Strength': 'TCS',
  'Rock Uniaxial Compressive Strength': 'UCS',
  'Rock Uniaxial Compressive Strength with Modulus': 'UCSM',
  'Point Load Test': 'PLT',
  'Cerchar Abrasivity Test': 'Cerchar',
  'Atterberg Limits': 'Atterberg',
  'Particle Size Distribution': 'PSD',
  'Grain Size Analysis': 'PSD',
  'Triaxial Test (CU)': 'TriaxialCU',
  'Triaxial Test (CD)': 'TriaxialCD',
  // Current Test Catalog display names (what the grids now show).
  'Rock Direct Shear': 'DS',
  'Brazilian Tensile Strength': 'BTS',
  'Rock Triaxial (Single Stage)': 'TCS',
  UCS: 'UCS',
  'UCS with Modulus': 'UCSM',
  'Point Load': 'PLT',
  Cerchar: 'Cerchar',
  'Sieve and Hydrometer': 'PSD',
  'Isotropically Consolidated Undrained Triaxial (CIU)': 'TriaxialCU',
  'Isotropically Consolidated Drained Triaxial (CID)': 'TriaxialCD',
};

const testTypeToken = (testName: string): string =>
  TEST_TYPE_SHORT[testName] ?? sanitizeFilenamePart(testName);

/** Report types whose PDF aggregates the WHOLE work order: every specimen row
 *  is its own WorkOrderDetail, but a single report table covers them all (Point
 *  Load, ASTM D5731 — DEC-01: backend stays one-sample-one-test, the report
 *  aggregates). Mirrors the backend sheetRegistry `scope: 'workOrderBatch'`
 *  used by the bulk test-sheet dedup (reportController). Selecting many rows of
 *  such a test must generate ONE report per work order, not one identical
 *  report per selected row. BUG-2026-0198.
 *
 *  BUG-2026-0197: keys on the stable `TestCode`, not the display name — the app
 *  now shows the Test Catalog name (e.g. "Point Load"), so a name-based check
 *  would silently stop deduping. */
export const WORK_ORDER_BATCH_REPORT_TEST_CODES: readonly string[] = [TEST_CODES.pointLoad];

/** True when a test's report aggregates the whole work order (see above). */
export const isWorkOrderBatchReport = (testCode: string | null | undefined): boolean =>
  !!testCode && WORK_ORDER_BATCH_REPORT_TEST_CODES.includes(testCode);

/** One fetched report headed into a per-type merged PDF. */
export interface TypedReportItem {
  blob: Blob;
  testName: string;
  workOrderId?: string | null;
}

/**
 * Bulk "Generate Reports" download: ONE merged PDF **per test type**, each
 * named `<WO-or-project>_<TestType>_<date>_<HHmm>.pdf` (e.g.
 * `WO-2026-110_TCS_2026-07-24_1435.pdf`). The scope token is the work order
 * when every report in that type-group shares one WO, else `fallbackScope`
 * (the project id, or 'Tests' on cross-project surfaces). Files download in
 * sequence — the browser may ask once to allow multiple downloads.
 * Returns the number of PDF files saved.
 */
export const saveMergedReportsByType = async (
  items: TypedReportItem[],
  fallbackScope: string,
): Promise<number> => {
  const now = new Date();
  const stamp = `${now.toISOString().slice(0, 10)}_`
    + `${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}`;

  const byType = new Map<string, TypedReportItem[]>();
  for (const item of items) {
    const list = byType.get(item.testName) ?? [];
    list.push(item);
    byType.set(item.testName, list);
  }

  let files = 0;
  for (const [testName, group] of byType) {
    const merged = group.length === 1 ? group[0].blob : await mergePdfBlobs(group.map((g) => g.blob));
    const wos = [...new Set(group.map((g) => g.workOrderId).filter((w): w is string => !!w))];
    const scope = sanitizeFilenamePart(wos.length === 1 ? wos[0] : fallbackScope) || 'Tests';
    saveBlobToDisk(merged, `${scope}_${testTypeToken(testName)}_${stamp}.pdf`);
    files += 1;
    // Small gap between programmatic downloads so the browser registers each.
    if (files < byType.size) await new Promise((r) => setTimeout(r, 400));
  }
  return files;
};

export const saveBlobToDisk = (blob: Blob, filename: string): void => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

export const downloadReport = async (workOrderDetailKey: string): Promise<void> => {
  const { blob, filename } = await fetchReportPdf(workOrderDetailKey);
  saveBlobToDisk(blob, filename);
};

/** Report archive history for one test (generated renders + issued copies). */
export const fetchReportHistory = async (
  workOrderDetailKey: string,
): Promise<ReportArtifactSummary[]> => {
  const response = await apiClient.get(`/workorderdetails/${workOrderDetailKey}/report/history`);
  return response.data;
};

/** Download one archived report PDF (integrity-verified server-side). */
export const downloadReportArtifact = async (
  workOrderDetailKey: string,
  artifactKey: string,
): Promise<void> => {
  const response = await apiClient.get(
    `/workorderdetails/${workOrderDetailKey}/report/artifacts/${artifactKey}/download`,
    { responseType: 'blob', timeout: 60000 },
  );
  const disposition = response.headers['content-disposition'];
  const match = disposition?.match(/filename="(.+)"/);
  saveBlobToDisk(
    new Blob([response.data], { type: 'application/pdf' }),
    match?.[1] || 'report.pdf',
  );
};

/** Per-test archive rollup for a list of tests. Server caps 200 keys per
 *  call; larger lists are chunked transparently. */
export const fetchReportArchiveRollup = async (
  workOrderDetailKeys: string[],
): Promise<ReportArchiveRollup[]> => {
  const out: ReportArchiveRollup[] = [];
  for (let i = 0; i < workOrderDetailKeys.length; i += 200) {
    const chunk = workOrderDetailKeys.slice(i, i + 200);
    const response = await apiClient.post('/report-archive/rollup', { workOrderDetailKeys: chunk });
    out.push(...response.data);
  }
  return out;
};

/** Newest-first archive rows across ALL tests (Tests page archive browser). */
export const fetchRecentReportArchive = async (opts: {
  limit: number;
  offset: number;
  kind?: ReportArtifactKind;
  search?: string;
}): Promise<{ rows: ReportArchiveEntry[]; total: number }> => {
  const response = await apiClient.get('/report-archive/recent', {
    params: {
      limit: opts.limit,
      offset: opts.offset,
      ...(opts.kind ? { kind: opts.kind } : {}),
      ...(opts.search ? { search: opts.search } : {}),
    },
  });
  return response.data;
};

/** Persist an engineer-edited PDF from the Preview & Edit dialog as an
 *  'issued' report artifact. Body is the raw PDF bytes. */
export const saveIssuedReport = async (
  workOrderDetailKey: string,
  pdf: Blob,
  filename: string,
  note?: string,
): Promise<void> => {
  await apiClient.post(
    `/workorderdetails/${workOrderDetailKey}/report/issued`,
    pdf,
    {
      params: { filename, ...(note ? { note } : {}) },
      headers: { 'Content-Type': 'application/pdf' },
      timeout: 120000,
    },
  );
};
